import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Select,
  InputLabel,
  FormControl,
  MenuItem,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import axios from "axios";
import toast from 'react-hot-toast';

const WithdrawDialog = ({
  open,
  onClose,
  initialCoin = "BTC",
  initialNetwork = "Bitcoin",
}) => {
  const [selectedCurrency, setSelectedCurrency] = useState(initialCoin);
  const [selectedNetwork, setSelectedNetwork] = useState(initialNetwork);
  const [withdrawDetails, setWithdrawDetails] = useState({
    address: "",
    amount: "",
    networkFee: 0,
  });
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  // fees settings
  const [fee, setFee] = useState(0);
  const [baseFee, setBaseFee] = useState(0);
  const [percentage, setPercentage] = useState(0);
  const [minFee, setMinFee] = useState(0);
  const [maxFee, setMaxFee] = useState(0);
  const [exchangeRate, setExchangeRate] = useState(0);
  const [sourceAddress, setSourceAddress] = useState("");

  // Static mapping of coins to their networks
  const coinNetworkMap = {
    BTC: ["Bitcoin"],
    ETH: ["ERC20"],
    USDT: ["ERC20", "TRC20", "BSC", "Polygon"],
    USDC: ["ERC20", "TRC20", "BSC", "Polygon"],
  };

  // Fetch balance when currency or network changes
  useEffect(() => {
    if (selectedCurrency && selectedNetwork) {
        // reset all fields
        setWithdrawDetails({
            address: "",
            amount: "",
            networkFee: 0,
        });
        setFee(0);
        setBalance(null);
        setBaseFee(0);
        setMinFee(0);
        setMaxFee(0);
        setPercentage(0);
        setExchangeRate(0);

      fetchBalance();
    }
  }, [selectedCurrency, selectedNetwork]);

  const fetchBalance = async () => {
    try {
      setLoading(true);
      const token = Cookies.get("token");
      const email = Cookies.get("email");
      const response = await axios.get(
        `/api/api/getWithdrawBalance/${email}/${selectedCurrency.toLowerCase()}/${selectedNetwork.toLowerCase()}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

      const getExchangeRate = await axios.get(`/api/api/card/exchangeRate/${selectedCurrency.toLowerCase()}`);
      if (getExchangeRate.data.status === "success") {
        const fetchedExchangeRate = getExchangeRate.data.rate;
        const fetchedDefaultExchangeRate = getExchangeRate.data.defaultRate;

        setExchangeRate(fetchedExchangeRate);

        setBalance(response.data.balance || 0);
        setBaseFee(response.data.baseFee || 0);
        setMinFee(response.data.minFee || 0);
        setMaxFee(response.data.maxFee || 0);
        setPercentage(response.data.percentage || 0);
        setSourceAddress(response.data.assetAddress || "");
      } else {
        console.error("Error fetching exchange rate:", getExchangeRate.data);
      }
    } catch (error) {
      console.error("Error fetching balance:", error);
      setBalance(0);
    } finally {
      setLoading(false);
    }
  };

  const handleCurrencyChange = (event) => {
    const newCurrency = event.target.value;
    setSelectedCurrency(newCurrency);
    setSelectedNetwork(coinNetworkMap[newCurrency][0]); // Default to the first network for the selected coin
  };

  const handleNetworkChange = (event) => {
    setSelectedNetwork(event.target.value);
  };

  const handleWithdrawChange = (field, value) => {
    setWithdrawDetails((prev) => ({ ...prev, [field]: value }));
  };

  // handle network fee calculation on amount change
  const handleAmountChange = (event) => {
    const amount = parseFloat(event.target.value) || 0; // Parse amount as float, default to 0 if invalid
  
    // Convert amount to USD
    const amountInUSD = amount / parseFloat(exchangeRate || 1); // Ensure exchangeRate is parsed
  
    let fee = 0;
  
    if (amount > 0) {
      // Calculate the fee based on the amount, percentage, and base fee
      fee = Math.max(
        parseFloat(baseFee || 0) + (parseFloat(percentage || 0) / 100) * amountInUSD,
        parseFloat(minFee || 0)
      );
  
      // Ensure the fee does not exceed the max fee
      if (parseFloat(maxFee || 0) > 0) {
        fee = Math.min(fee, parseFloat(maxFee));
      }
    }
  
    // Convert fee to coin
    let feeInCoin = fee * parseFloat(exchangeRate || 1);
    feeInCoin = parseFloat(feeInCoin).toFixed(8); // Ensure precision up to 8 decimal places
  
    setFee(feeInCoin);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const token = Cookies.get("token");
      const email = Cookies.get("email");
      const response = await axios.post(
        `/api/api/withdrawCoin`,
        {
            coin: selectedCurrency,
          network: selectedNetwork,
          sourceAddress: sourceAddress,
            destinationAddress: withdrawDetails.address,
          amount: withdrawDetails.amount,
          email: email,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      onClose();
      toast.success("Withdraw request submitted successfully!");
    } catch (error) {
      console.error("Error submitting withdrawal:", error);
        toast.error("Error submitting withdrawal request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Backdrop
        open={loading}
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Dialog open={open} onClose={onClose}>
        <DialogTitle>Withdraw Funds</DialogTitle>
        <DialogContent>
          {/* Coin Selection */}
          <FormControl fullWidth margin="normal">
            <InputLabel
              sx={{
                backgroundColor: "#fff", // Set background color to white
                px: 1, // Add padding to prevent overlap with the border
              }}
            >
              Coin
            </InputLabel>
            <Select value={selectedCurrency} onChange={handleCurrencyChange}>
              {Object.keys(coinNetworkMap).map((coin) => (
                <MenuItem key={coin} value={coin}>
                  {coin}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Network Selection */}
          <FormControl fullWidth margin="normal">
            <InputLabel
              sx={{
                backgroundColor: "#fff", // Set background color to white
                px: 1, // Add padding to prevent overlap with the border
              }}
            >
              Coin
            </InputLabel>
            <Select value={selectedNetwork} onChange={handleNetworkChange}>
              {coinNetworkMap[selectedCurrency]?.map((network) => (
                <MenuItem key={network} value={network}>
                  {network}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Display Balance */}
          {selectedCurrency && selectedNetwork && (
            <Typography variant="body1" sx={{ mt: 2 }}>
              Balance:{" "}
              {balance !== null
                ? `${balance} ${selectedCurrency}`
                : "Loading..."}
            </Typography>
          )}

          {/* Withdraw Details */}
          <TextField
            fullWidth
            margin="normal"
            label="Wallet Address"
            value={withdrawDetails.address}
            onChange={(e) => handleWithdrawChange("address", e.target.value)}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Amount"
            type="number"
            value={withdrawDetails.amount}
            onChange={(e) => {
                handleWithdrawChange("amount", e.target.value);
                handleAmountChange(e); // Call the amount change handler to update fee
                }
            }
          />
          <Typography variant="body2" color="text.secondary" mt={2}>
            Network Fee: {fee} {selectedCurrency}
          </Typography>
          <Typography variant="body1" color="text.secondary" mt={2}>
            You will receive:{" "}
            {withdrawDetails.amount - fee} {selectedCurrency}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={
              !selectedCurrency ||
              !selectedNetwork ||
              !withdrawDetails.address ||
              !withdrawDetails.amount ||
              parseFloat(withdrawDetails.amount) - parseFloat(fee) <= 0 ||
              parseFloat(withdrawDetails.amount) > parseFloat(balance)
            }
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WithdrawDialog;
